<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { orderUseCases } from '@/modules/order/composition';
import type { OrderSummary } from '@/modules/order/domain/entities/Order';

const route = useRoute();
const authStore = useAuthStore();
const { t } = useI18n();

const recoverableCount = ref(0);
const nearestRecoverableUntil = ref<string | null>(null);

const isVisible = computed(() => authStore.isAuthenticated && recoverableCount.value > 0);

const formatDeadline = (dateString: string | null): string => {
  if (!dateString) {
    return t('orderRecovery.notice.asSoonAsPossible');
  }

  return new Date(dateString).toLocaleString();
};

const fetchRecoverableOrders = async (): Promise<void> => {
  if (!authStore.isAuthenticated) {
    recoverableCount.value = 0;
    nearestRecoverableUntil.value = null;
    return;
  }

  try {
    const filters = { status: 'pending' as const, perPage: 50 };
    const firstPage = await orderUseCases.getOrders.execute(filters, 1);
    const lastPage = firstPage.meta?.lastPage ?? 1;

    const otherPages =
      lastPage > 1
        ? await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, index) =>
              orderUseCases.getOrders.execute(filters, index + 2)
            )
          )
        : [];

    const pendingOrders = [
      ...firstPage.orders,
      ...otherPages.flatMap((pageResult) => pageResult.orders),
    ];

    const recoverableOrders = pendingOrders.filter((order: OrderSummary) => order.isRecoverable);

    recoverableCount.value = recoverableOrders.length;

    nearestRecoverableUntil.value = recoverableOrders
      .map((order) => order.recoverableUntil)
      .filter((value): value is string => typeof value === 'string')
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;
  } catch {
    recoverableCount.value = 0;
    nearestRecoverableUntil.value = null;
  }
};

watch(
  () => route.fullPath,
  () => {
    void fetchRecoverableOrders();
  }
);

watch(
  () => authStore.isAuthenticated,
  () => {
    void fetchRecoverableOrders();
  }
);

onMounted(() => {
  void fetchRecoverableOrders();
});
</script>

<template>
  <div
    v-if="isVisible"
    class="border-b border-gold-400/30 bg-gold-400/10 px-4 py-3"
    role="status"
    aria-live="polite"
  >
    <div class="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-2">
      <p class="text-sm text-cream-100 font-sans">
        {{ t('orderRecovery.notice.message', { count: recoverableCount }) }}
        <span class="text-gold-300 font-medium">
          {{ t('orderRecovery.notice.deadline', { date: formatDeadline(nearestRecoverableUntil) }) }}
        </span>
      </p>

      <router-link
        to="/my-orders"
        class="text-sm font-semibold text-gold-300 hover:text-gold-200 transition-colors"
      >
        {{ t('orderRecovery.notice.cta') }}
      </router-link>
    </div>
  </div>
</template>
